/**
 * System health card component
 * Displays system status on the dashboard
 */

import { Card, Typography, Space, Badge, Skeleton, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  ApiOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { SystemStats } from '@/types/api';

const { Text } = Typography;

interface SystemHealthCardProps {
  stats?: SystemStats;
  loading?: boolean;
}

type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

interface HealthItemProps {
  label: string;
  status: HealthStatus;
  value?: string;
  icon: React.ReactNode;
}

function HealthItem({ label, status, value, icon }: HealthItemProps) {
  const statusConfig: Record<HealthStatus, { color: string; icon: React.ReactNode; text: string }> = {
    healthy: {
      color: '#52c41a',
      icon: <CheckCircleOutlined />,
      text: 'Healthy',
    },
    degraded: {
      color: '#faad14',
      icon: <WarningOutlined />,
      text: 'Degraded',
    },
    unhealthy: {
      color: '#ff4d4f',
      icon: <CloseCircleOutlined />,
      text: 'Unhealthy',
    },
    unknown: {
      color: '#8c8c8c',
      icon: <WarningOutlined />,
      text: 'Unknown',
    },
  };

  const config = statusConfig[status];

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <Space size={8}>
        <span style={{ color: 'var(--color-text-tertiary)', fontSize: 16 }}>{icon}</span>
        <Text>{label}</Text>
      </Space>
      <Tooltip title={config.text}>
        <Space size={4}>
          {value && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {value}
            </Text>
          )}
          <span style={{ color: config.color }}>{config.icon}</span>
        </Space>
      </Tooltip>
    </div>
  );
}

export function SystemHealthCard({ stats, loading }: SystemHealthCardProps) {
  if (loading) {
    return (
      <Card title="System Health">
        <Skeleton active paragraph={{ rows: 5 }} />
      </Card>
    );
  }

  const getWorkerStatus = (): HealthStatus => {
    if (!stats?.workers) return 'unknown';
    const { active, total } = stats.workers;
    if (total === 0) return 'healthy'; // No workers configured, nothing wrong
    if (active === total) return 'healthy';
    if (active > 0) return 'degraded';
    return 'unhealthy';
  };

  const getQueueStatus = (): HealthStatus => {
    if (!stats?.queue) return 'unknown';
    const { waiting, active, failed } = stats.queue;
    if (failed > 10) return 'degraded';
    return 'healthy';
  };

  const getDatabaseStatus = (): HealthStatus => {
    if (!stats?.database) return 'unknown';
    return stats.database.connected ? 'healthy' : 'unhealthy';
  };

  const getCacheStatus = (): HealthStatus => {
    if (!stats?.cache) return 'unknown';
    return stats.cache.connected ? 'healthy' : 'unhealthy';
  };

  const getProxyStatus = (): HealthStatus => {
    if (!stats?.proxies) return 'unknown';
    const { healthy, total } = stats.proxies;
    if (!total || total === 0) return 'unknown';
    const ratio = healthy / total;
    if (ratio >= 0.8) return 'healthy';
    if (ratio >= 0.5) return 'degraded';
    return 'unhealthy';
  };

  return (
    <Card
      title="System Health"
      styles={{ body: { padding: '8px 24px' } }}
    >
      <HealthItem
        label="Workers"
        status={getWorkerStatus()}
        value={stats?.workers ? `${stats.workers.active}/${stats.workers.total} Active` : undefined}
        icon={<TeamOutlined />}
      />
      <HealthItem
        label="Queue"
        status={getQueueStatus()}
        value={stats?.queue ? `${stats.queue.waiting} pending` : undefined}
        icon={<CloudServerOutlined />}
      />
      <HealthItem
        label="Database"
        status={getDatabaseStatus()}
        value={stats?.database?.connected ? 'Connected' : 'Disconnected'}
        icon={<DatabaseOutlined />}
      />
      <HealthItem
        label="Cache (Valkey)"
        status={getCacheStatus()}
        value={stats?.cache?.connected ? 'Connected' : 'Disconnected'}
        icon={<ApiOutlined />}
      />
      <div style={{ borderBottom: 'none' }}>
        <HealthItem
          label="Proxies"
          status={getProxyStatus()}
          value={
            stats?.proxies?.total
              ? `${stats.proxies.healthy}/${stats.proxies.total} healthy`
              : 'Not configured'
          }
          icon={<CloudServerOutlined />}
        />
      </div>
    </Card>
  );
}

export default SystemHealthCard;
